import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar,
  Clock,
  Send,
  CheckCircle2,
  Lock,
  Home,
  Building2,
  Phone,
  Camera,
  X,
} from "lucide-react";
import {
  SATURDAY_SLOTS as TIME_SLOTS,
  getUpcomingWorkDays,
  formatISODate,
} from "../lib/schedule";
import { computeQuote, MINIMUM_FEE } from "../lib/towns";
import { fileToResizedDataUri } from "../lib/imageResize";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTOS = 6;

export default function RequestForm({ quoteState }) {
  const upcoming = useMemo(() => getUpcomingWorkDays(12), []);
  const [unavailable, setUnavailable] = useState([]);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    property_type: "residential",
    notes: "",
    preferred_date_note: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [photos, setPhotos] = useState([]); // array of data URIs
  const [photoBusy, setPhotoBusy] = useState(false);

  const addPhotos = async (fileList) => {
    const incoming = Array.from(fileList || []).filter((f) =>
      f.type.startsWith("image/")
    );
    if (!incoming.length) return;
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) {
      toast.error(`Maximum ${MAX_PHOTOS} photos.`);
      return;
    }
    setPhotoBusy(true);
    try {
      const next = [];
      for (const file of incoming.slice(0, room)) {
        try {
          const uri = await fileToResizedDataUri(file, { maxDim: 1600 });
          next.push(uri);
        } catch (e) {
          toast.error(`Could not read ${file.name}`);
        }
      }
      setPhotos((prev) => [...prev, ...next]);
    } finally {
      setPhotoBusy(false);
    }
  };

  const removePhoto = (idx) => {
    setPhotos((prev) => prev.filter((_, i) => i !== idx));
  };

  useEffect(() => {
    axios
      .get(`${API}/bookings/unavailable`)
      .then((r) => setUnavailable(r.data.unavailable || []))
      .catch(() => setUnavailable([]));
  }, []);

  const q = useMemo(
    () => (quoteState ? computeQuote(quoteState) : null),
    [quoteState]
  );

  const isSlotUnavailable = (date, slot) =>
    unavailable.some(
      (u) => u.date === date && (u.slot === slot || u.slot === "ALL")
    );
  const isDayFullyBlocked = (date) =>
    unavailable.some((u) => u.date === date && u.slot === "ALL") ||
    TIME_SLOTS.every((s) =>
      unavailable.some((u) => u.date === date && u.slot === s)
    );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address) {
      toast.error("Please fill in name, phone, and address.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (photos.length) payload.photos = photos;
      if (q && form.property_type === "residential") {
        payload.breakdown = {
          minimum_fee: MINIMUM_FEE,
          single_story_outside_windows: quoteState.singleOut,
          inside_windows: quoteState.inside,
          second_story_outside_windows: quoteState.secondOut,
          single_story_outside_total: q.singleOutTotal,
          inside_total: q.insideTotal,
          second_story_total: q.secondOutTotal,
          window_subtotal: q.windowSubtotal,
          service_town: quoteState.town?.name || "San Angelo",
          one_way_miles:
            quoteState.town?.oneWay === -1
              ? Number(quoteState.manualMiles || 0)
              : quoteState.town?.oneWay || 0,
          round_trip_miles: q.roundTrip,
          mileage_fee: q.mileageFee,
          minimum_applied: q.minimumApplied,
          estimated_total: q.total,
        };
      }
      const r = await axios.post(`${API}/quotes`, payload);
      setDone(true);
      toast.success("Request sent!", { description: r.data.message });
    } catch (err) {
      toast.error("Could not send request", {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section
        id="request"
        className="bg-white py-24 md:py-32 border-t border-black/5"
      >
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <CheckCircle2 className="h-14 w-14 text-[#1A365D] mx-auto" />
          <h2 className="mt-6 font-serif text-4xl md:text-5xl text-[#111827]">
            Request sent &mdash;{" "}
            <span className="italic text-[#1A365D]">thank you.</span>
          </h2>
          <p className="mt-5 text-[#4B5563] font-light text-lg leading-relaxed">
            Your details are on their way to joseph@windowshinetx.com.
            We&apos;ll be in touch shortly to confirm
            your quote and schedule a Mon&ndash;Sat visit (9&nbsp;AM &ndash;
            3&nbsp;PM). Or give us a call at{" "}
            <a href="tel:+16085772761" className="text-[#1A365D] underline">
              608-577-2761
            </a>
            .
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      id="request"
      className="bg-white py-24 md:py-32 border-t border-black/5"
      data-testid="request-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#1A365D] mb-4">
              Send your details
            </div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#111827] leading-tight">
              Share your address.
              <br />
              <span className="italic text-[#1A365D]">
                We&apos;ll call to confirm.
              </span>
            </h2>
          </div>
          <p className="max-w-md text-[#4B5563] font-light leading-relaxed">
            Prefer to talk? Call{" "}
            <a
              href="tel:+16085772761"
              className="text-[#1A365D] underline underline-offset-4 hover:text-[#2B4C7E]"
            >
              608-577-2761
            </a>
            . Otherwise, send your info below and we&apos;ll reach out
            Mon&ndash;Sat to confirm your free quote.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* View-only availability */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 rounded-3xl bg-[#F9F9F6] border border-black/5 p-8"
            data-testid="availability-calendar"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#E8F1F2] text-[#1A365D]">
                <Calendar className="h-5 w-5" />
              </span>
              <div>
                <div className="font-serif text-2xl text-[#111827]">
                  Upcoming availability
                </div>
                <div className="text-sm text-[#4B5563]">
                  Mon&ndash;Sat &middot; view-only.
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {upcoming.slice(0, 8).map((d) => {
                const iso = formatISODate(d);
                const blocked = isDayFullyBlocked(iso);
                return (
                  <div
                    key={iso}
                    className={`rounded-2xl border p-4 relative ${
                      blocked
                        ? "bg-black/5 border-transparent opacity-50"
                        : "bg-white border-black/10"
                    }`}
                    data-testid={`view-date-${iso}`}
                  >
                    <div className="text-xs uppercase tracking-[0.15em] opacity-70">
                      {d.toLocaleDateString("en-US", { month: "short" })}
                    </div>
                    <div className="font-serif text-3xl leading-none mt-1 text-[#111827]">
                      {d.getDate()}
                    </div>
                    <div className="text-xs mt-2 opacity-80 text-[#4B5563]">
                      {d.toLocaleDateString("en-US", { weekday: "long" })}
                    </div>
                    {blocked && (
                      <div className="absolute top-2 right-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.15em] bg-white/80 text-[#4B5563] px-1.5 py-0.5 rounded-full">
                        <Lock className="h-2.5 w-2.5" />
                        Booked
                      </div>
                    )}
                    <div className="mt-3 flex flex-col gap-1">
                      {TIME_SLOTS.map((slot) => {
                        const off = isSlotUnavailable(iso, slot);
                        return (
                          <div
                            key={slot}
                            className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md flex items-center justify-between gap-1 ${
                              off
                                ? "bg-black/5 text-[#4B5563]"
                                : "bg-[#E8F1F2] text-[#1A365D]"
                            }`}
                          >
                            <span className={off ? "line-through opacity-70" : ""}>
                              {slot.split(" - ")[0]}
                            </span>
                            {off && (
                              <span className="text-[8px] tracking-[0.2em] opacity-80">
                                Booked
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 flex items-center gap-4 text-xs text-[#4B5563]">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-[#1A365D]" />
                Available
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-black/20" />
                Booked
              </span>
            </div>
          </motion.div>

          {/* Form */}
          <motion.form
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            onSubmit={submit}
            className="lg:col-span-7 rounded-3xl bg-[#F9F9F6] border border-black/5 p-8 md:p-10"
            data-testid="quote-request-form"
          >
            <div className="font-serif text-2xl text-[#111827] mb-6">
              Your details
            </div>

            <div className="mb-6">
              <label className="block text-xs uppercase tracking-[0.15em] text-[#4B5563] mb-2">
                Property type
              </label>
              <div className="inline-flex rounded-full border border-black/10 bg-white p-1">
                <PropertyPill
                  active={form.property_type === "residential"}
                  onClick={() =>
                    setForm({ ...form, property_type: "residential" })
                  }
                  icon={<Home className="h-4 w-4" />}
                  label="Residential"
                  testid="property-residential"
                />
                <PropertyPill
                  active={form.property_type === "commercial"}
                  onClick={() =>
                    setForm({ ...form, property_type: "commercial" })
                  }
                  icon={<Building2 className="h-4 w-4" />}
                  label="Commercial"
                  testid="property-commercial"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label="Name *"
                value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
                testid="form-name"
              />
              <Field
                label="Phone *"
                type="tel"
                value={form.phone}
                onChange={(v) => setForm({ ...form, phone: v })}
                testid="form-phone"
              />
              <Field
                label="Email"
                type="email"
                value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
                testid="form-email"
              />
              <Field
                label="Address *"
                value={form.address}
                onChange={(v) => setForm({ ...form, address: v })}
                testid="form-address"
                placeholder="Street, City, TX"
              />
            </div>

            <div className="mt-4">
              <label className="block text-xs uppercase tracking-[0.15em] text-[#4B5563] mb-2">
                Preferred day/time (optional)
              </label>
              <input
                type="text"
                placeholder="e.g. any Saturday morning"
                value={form.preferred_date_note}
                onChange={(e) =>
                  setForm({ ...form, preferred_date_note: e.target.value })
                }
                className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#1A365D]/30"
                data-testid="form-preferred-date"
              />
            </div>

            <div className="mt-4">
              <label className="block text-xs uppercase tracking-[0.15em] text-[#4B5563] mb-2">
                Notes (floors, gate codes, anything we should know)
              </label>
              <textarea
                rows={4}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#1A365D]/30"
                data-testid="form-notes"
              />
            </div>

            {/* Photo upload */}
            <div className="mt-4" data-testid="photo-upload-block">
              <div className="flex items-center justify-between gap-3 mb-2">
                <label className="block text-xs uppercase tracking-[0.15em] text-[#4B5563]">
                  Photos (optional)
                </label>
                <span className="text-[11px] text-[#4B5563]">
                  {photos.length}/{MAX_PHOTOS}
                </span>
              </div>
              <p className="text-xs text-[#4B5563] leading-relaxed mb-3">
                A few snaps help us give you a clearer estimate — especially
                for homes outside San Angelo. Phone photos work great.
              </p>

              <label
                className={`flex items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-[#1A365D]/25 bg-white py-6 cursor-pointer hover:bg-[#E8F1F2]/40 transition-colors ${
                  photos.length >= MAX_PHOTOS || photoBusy
                    ? "opacity-60 pointer-events-none"
                    : ""
                }`}
                data-testid="photo-dropzone"
              >
                <Camera className="h-5 w-5 text-[#1A365D]" />
                <span className="text-sm uppercase tracking-[0.15em] text-[#1A365D]">
                  {photoBusy
                    ? "Processing…"
                    : photos.length
                    ? "Add more photos"
                    : "Tap to add photos"}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    addPhotos(e.target.files);
                    e.target.value = "";
                  }}
                  className="hidden"
                  data-testid="photo-input"
                />
              </label>

              {photos.length > 0 && (
                <div
                  className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2"
                  data-testid="photo-thumbs"
                >
                  {photos.map((uri, i) => (
                    <div
                      key={i}
                      className="relative group aspect-square overflow-hidden rounded-xl border border-black/10 bg-white"
                    >
                      <img
                        src={uri}
                        alt={`Window photo ${i + 1}`}
                        className="h-full w-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(i)}
                        className="absolute top-1.5 right-1.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/95 text-[#1A365D] shadow hover:bg-white"
                        aria-label={`Remove photo ${i + 1}`}
                        data-testid={`remove-photo-${i}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Rough-quote summary when available (residential + user filled calculator) */}
            {q && form.property_type === "residential" && (
              <div
                className="mt-6 rounded-2xl bg-white border border-black/10 p-5"
                data-testid="form-summary-rough"
              >
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-[0.15em] text-[#4B5563] mb-1">
                      Your rough estimate
                    </div>
                    <div
                      className="font-serif text-4xl text-[#1A365D]"
                      data-testid="form-summary-total"
                    >
                      ${q.total.toFixed(0)}
                    </div>
                  </div>
                  <div className="text-right text-xs text-[#4B5563] max-w-[200px]">
                    <Clock className="h-4 w-4 inline mr-1 text-[#1A365D]" />
                    Confirmed on-site. $99 minimum visit fee.
                  </div>
                </div>
                <p className="mt-3 text-xs text-[#4B5563] leading-relaxed">
                  Didn&apos;t use the calculator? No worries &mdash; just send
                  your info and we&apos;ll quote on the call. Prefer to talk
                  now?{" "}
                  <a
                    href="tel:+16085772761"
                    className="text-[#1A365D] underline underline-offset-2"
                  >
                    608-577-2761
                  </a>
                  .
                </p>
              </div>
            )}

            {form.property_type === "commercial" && (
              <div className="mt-6 rounded-2xl bg-white border border-black/10 p-5 flex items-start gap-3">
                <Phone className="h-5 w-5 text-[#1A365D] shrink-0 mt-0.5" />
                <p className="text-sm text-[#4B5563] leading-relaxed">
                  Commercial jobs are quoted individually &mdash; send your
                  details and we&apos;ll call to set up a quick on-site walk.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-6 inline-flex items-center justify-center gap-2 w-full rounded-full bg-[#1A365D] text-white uppercase tracking-[0.18em] text-sm py-4 hover:bg-[#2B4C7E] transition-colors disabled:opacity-60"
              data-testid="form-submit-button"
            >
              {submitting ? (
                "Sending…"
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Send my request
                </>
              )}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function PropertyPill({ active, onClick, icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-[0.15em] transition-colors ${
        active
          ? "bg-[#1A365D] text-white"
          : "text-[#4B5563] hover:text-[#1A365D]"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function Field({ label, value, onChange, type = "text", testid, placeholder }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.15em] text-[#4B5563] mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#1A365D]/30"
        data-testid={testid}
      />
    </div>
  );
}
